import { mat4, vec4 } from "gl-matrix";
import { Quad } from "./Quad";
import { PostEffect } from "./PostEffect";

export interface PostEffectsInput {
  now: number;
  resolution: [number, number];

  // For fade
  fadeColor: vec4;

  enableEffects?: { [name: string]: true };
}

/**
 * PostEffectsPipeline is used for chaining post effects together.
 * We can't put every effect into one shader, because many of them need to
 * sample the screen buffer, which would overwrite the previous effect.
 * Instead, we'll render each effect to a texture (using a framebuffer),
 * and then render the texture to the screen with the next effect.
 */
export class PostEffectsPipeline {
  /**
   * This is used for the normal scene rendering, which produces a color buffer
   * and a depth buffer.
   */
  private readonly sceneFramebuffer: WebGLFramebuffer;
  /**
   * This is used for the post effects rendering, which produces a color buffer and
   * feeds it into the next effect.
   */
  private readonly effectFramebuffer: WebGLFramebuffer;

  // We need two copies of the color texture because each effect will render
  // to one and then we'll swap them.
  // We use 16-float textures to enable HDR rendering.
  private readonly colorTextures: WebGLTexture[] = [];
  private colorTextureIndex = 0;

  // We also need a depth texture for the scene rendering. One is enough because
  // effects do not alter the depth buffer.
  // This depth texture is 32-bit float, because it also stores the position of each fragment.
  private readonly positionAndDepthTexture: WebGLTexture;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly quad: Quad,
    originalResolution: [number, number],
    protected readonly resolutionScale: number,
    protected readonly renderLastToScreen: boolean,
    private readonly effects: PostEffect[],
  ) {
    const resolution = [
      Math.floor(originalResolution[0] * resolutionScale),
      Math.floor(originalResolution[1] * resolutionScale),
    ] as [number, number];
    // Create color textures
    for (let i = 0; i < 2; i++) {
      const colorTexture = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, colorTexture);
      this.initializeColorTexture(colorTexture, resolution);
      this.setTextureFlags();
      this.colorTextures.push(colorTexture);
    }

    // Create depth texture
    this.positionAndDepthTexture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.positionAndDepthTexture);
    this.initializePositionAndDepthTexture(
      this.positionAndDepthTexture,
      resolution,
    );
    this.setTextureFlags();

    gl.bindTexture(gl.TEXTURE_2D, null);

    // Create scene framebuffer
    this.sceneFramebuffer = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFramebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.colorTextures[this.colorTextureIndex],
      0,
    );
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT1,
      gl.TEXTURE_2D,
      this.positionAndDepthTexture,
      0,
    );
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

    // Create effect framebuffer
    this.effectFramebuffer = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.effectFramebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.colorTextures[1 - this.colorTextureIndex],
      0,
    );
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  public beforeScene() {
    const gl = this.gl;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFramebuffer);
    // Update framebuffer texture
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.colorTextures[this.colorTextureIndex],
      0,
    );
  }

  public getScreenBuffer() {
    return this.colorTextures[this.colorTextureIndex];
  }

  public getBackBuffer() {
    return this.colorTextures[1 - this.colorTextureIndex];
  }

  public copyFromScreenBuffer() {
    const gl = this.gl;

    gl.bindTexture(gl.TEXTURE_2D, this.colorTextures[this.colorTextureIndex]);
    gl.copyTexSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      0,
      0,
      gl.drawingBufferWidth,
      gl.drawingBufferHeight,
    );
  }

  public render(input: PostEffectsInput) {
    const gl = this.gl;

    const renderQuad = () => {
      this.quad.render();
    };

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.effectFramebuffer);

    const renderUpTo = this.renderLastToScreen
      ? this.effects.length - 1
      : this.effects.length;

    // Render up to second-to-last effect
    for (let i = 0; i < renderUpTo; i++) {
      const effect = this.effects[i];

      if (!effect.enabled && !input.enableEffects?.[effect.name]) {
        continue;
      }

      const colorTexture = this.colorTextures[this.colorTextureIndex];
      const depthTexture = this.positionAndDepthTexture;
      effect.render(input, colorTexture, depthTexture, renderQuad);

      // Swap color textures in the framebuffer
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        this.colorTextures[this.colorTextureIndex],
        0,
      );
      this.colorTextureIndex = 1 - this.colorTextureIndex;
    }

    if (this.renderLastToScreen) {
      // Last effect is rendered to the screen
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      const effect = this.effects[this.effects.length - 1];

      const colorTexture = this.colorTextures[this.colorTextureIndex];
      const depthTexture = this.positionAndDepthTexture;
      effect.render(input, colorTexture, depthTexture, renderQuad);
    }
  }

  public getEffect(name: string): PostEffect | undefined {
    return this.effects.find((effect) => effect.name === name);
  }

  public setEffectEnabled(name: string, enabled: boolean) {
    const effect = this.effects.find((effect) => effect.name === name);
    if (effect) {
      effect.enabled = enabled;
    }
  }

  initializeColorTexture(
    texture: WebGLTexture,
    [width, height]: [number, number],
  ) {
    const gl = this.gl;

    gl.bindTexture(this.gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA16F,
      width,
      height,
      0,
      gl.RGBA,
      gl.FLOAT,
      null,
    );
  }

  initializePositionAndDepthTexture(
    texture: WebGLTexture,
    [width, height]: [number, number],
  ) {
    const gl = this.gl;

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      width,
      height,
      0,
      gl.RGBA,
      gl.FLOAT,
      null,
    );
  }

  setTextureFlags() {
    const gl = this.gl;

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  resize(originalResolution: [number, number]) {
    const gl = this.gl;

    const resolution = [
      Math.floor(originalResolution[0] * this.resolutionScale),
      Math.floor(originalResolution[1] * this.resolutionScale),
    ] as [number, number];

    // Resize color textures
    for (const texture of this.colorTextures) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      this.initializeColorTexture(texture, resolution);
    }

    // Resize depth texture
    gl.bindTexture(gl.TEXTURE_2D, this.positionAndDepthTexture);
    this.initializePositionAndDepthTexture(
      this.positionAndDepthTexture,
      resolution,
    );

    gl.bindTexture(gl.TEXTURE_2D, null);
  }
}
